import api from '../api/index';

const LOCAL_USER_KEY = 'localAuthUser';
const MSAL_ROLE_KEY = 'msalUserRole';

export interface LocalUser {
    userId: string;
    username: string;
    email: string;
    fullName: string;
    role: number;
    isAdmin: boolean;
}

const toBool = (value: any): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    if (typeof value === 'number') return value === 1;
    return false;
};

const normalizeLocalUser = (raw: any): LocalUser => ({
    userId: raw?.userId || raw?.UserId || '',
    username: raw?.username || raw?.Username || '',
    email: raw?.email || raw?.Email || '',
    fullName: raw?.fullName || raw?.FullName || '',
    role: Number(raw?.role ?? raw?.Role ?? 0),
    isAdmin: toBool(raw?.isAdmin ?? raw?.IsAdmin),
});

export const localLogin = async (username: string, password: string): Promise<LocalUser> => {
    const response = await api.post('/localauth/login', { username, password });
    const user = normalizeLocalUser(response.data || {});
    localStorage.setItem(LOCAL_USER_KEY, JSON.stringify(user));
    return user;
};

export const getLocalUser = (): LocalUser | null => {
    const raw = localStorage.getItem(LOCAL_USER_KEY);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        return normalizeLocalUser(parsed);
    } catch {
        return null;
    }
};

export const isLocallyAuthenticated = (): boolean => {
    return getLocalUser() !== null;
};

export const localLogout = (): void => {
    localStorage.removeItem(LOCAL_USER_KEY);
    localStorage.removeItem(MSAL_ROLE_KEY);
};

export const getMsalUserRole = (): number | null => {
    const raw = localStorage.getItem(MSAL_ROLE_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
};

export const fetchAndCacheMsalRole = async (): Promise<void> => {
    try {
        const response = await api.get('/localauth/me');
        const role = response.data?.role;
        if (role !== undefined && role !== null) {
            localStorage.setItem(MSAL_ROLE_KEY, String(role));
        }
    } catch {
        // silently ignore — role remains unknown until next attempt
    }
};

export const changePassword = async (userId: string, currentPassword: string, newPassword: string): Promise<void> => {
    await api.post('/localauth/change-password', { userId, currentPassword, newPassword });
};

export const useLocalAuth = (): boolean => {
    return (
        process.env.REACT_APP_BYPASS_MSAL === 'true' ||
        process.env.NODE_ENV === 'development'
    );
};
